import styled, { css, DefaultTheme, keyframes } from 'styled-components';
import {
    alpha,
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    FOCUS_OUTLINE_STYLE,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
} from '@optimus-web/common';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Stack } from '../Stack';
import { Tooltip } from '../Tooltip';
import { AppliedModifiers } from './types';

const DAYS_NAME_SIZE_HEIGHT = 52;
const DAY_NAME_SIZE_WIDTH = 46;
const MONTH_GAP = 6;
const YEAR_LABEL_WIDTH = 45;

const DAY_SIZE_WIDTH = 48;
const HORIZONTAL_PADDING = 8;
export const MONTH_WIDTH = 7 * DAY_SIZE_WIDTH + HORIZONTAL_PADDING * 2;

const getCalendarHeightByWeeks = (numberOfWeeks: number, theme: DefaultTheme) =>
    getToken('sizing900')({ theme }) +
    DAYS_NAME_SIZE_HEIGHT +
    numberOfWeeks * getToken('sizing500')({ theme }) +
    getSpacingToken('50')({ theme });

const getMonthSelectorHeightByWeeks = (numberOfWeeks: number, theme: DefaultTheme) =>
    DAYS_NAME_SIZE_HEIGHT + numberOfWeeks * getToken('sizing500')({ theme }) + getSpacingToken('50')({ theme });

interface CalendarViewHolderProps {
    numberOfWeeks: number;
    standalone?: boolean;
    isMobile?: boolean;
    theme: DefaultTheme;
}

export const CalendarViewHolderElement = styled.div<CalendarViewHolderProps>`
    display: ${({ isMobile }) => (isMobile ? 'flex' : 'inline-flex')};
    border-radius: ${getToken('borderRadius300')}px;
    overflow: hidden;
    position: relative;
    min-width: ${({ isMobile }) => (isMobile ? '360' : MONTH_WIDTH)}px;
    height: ${({ standalone, numberOfWeeks, theme }) => getTransformHeight(standalone)(numberOfWeeks, theme)}px;
    ${({ isMobile }) => isMobile && 'justify-content: center; width: 100%;'}
`;

export const CalendarMonthGridContainerElement = styled.div`
    position: relative;
    overflow: hidden;
`;

export const PrevMonthButtonWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${getToken('backgroundStaticFlat')};
    z-index: 30;
`;

export const PrevMonthIconButton = styled(IconButton)`
    position: relative;
    margin: ${getSpacingToken('200')}px 0 0 ${getSpacingToken('200')}px;
    ${({ disabled }) => disabled && 'display: none;'}
`;

export const NextMonthButtonWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    background-color: ${getToken('backgroundStaticFlat')};
    z-index: 30;
`;

export const NextMonthIconButton = styled(IconButton)`
    position: relative;
    margin: ${getSpacingToken('200')}px ${getSpacingToken('200')}px 0 0;
    ${({ disabled }) => disabled && 'display: none;'}
`;

export const MonthGridElement = styled.div`
    display: flex;
`;

export const MonthElement = styled.div<{ isMobile?: boolean }>`
    width: ${({ isMobile }) => (isMobile ? '100%' : `${MONTH_WIDTH}px`)};
    box-sizing: border-box;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;

    & + & {
        border-left: ${getToken('borderWidth100')}px solid ${getToken('borderStaticPrimary')};
    }
`;

export const WeeksContainerElement = styled.div<{ isMobile?: boolean }>`
    margin-top: ${getSpacingToken('500')}px;
    margin-bottom: ${getSpacingToken('100')}px;
    ${({ isMobile }) => isMobile && 'width: 100%;'}
`;

export const WeekElement = styled.div`
    display: flex;
    justify-content: space-between;
`;

export const MonthButtonContainerElement = styled.div<{ monthSelectorExpanded?: boolean }>`
    height: ${getToken('sizing900')}px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: ${getSpacingToken('100')}px;
    width: 100%;
    text-align: center;
    background-color: ${getToken('backgroundStaticFlat')};
    z-index: 20;
    position: relative;
    border-bottom: ${getToken('borderWidth100')}px solid ${getToken('borderStaticPrimary')};

    ${({ monthSelectorExpanded }) =>
        monthSelectorExpanded &&
        css`
            > button {
                background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
            }
        `}
`;

export const DayNamesElement = styled.div<{ isMobile?: boolean }>`
    ${getTextTokenPreset('bodySmallStrong')};
    position: absolute;
    top: ${getToken('sizing900')}px;
    height: ${DAYS_NAME_SIZE_HEIGHT}px;
    display: flex;
    align-items: center;
    ${({ isMobile }) => isMobile && 'justify-content: center; width: 100%;'}
`;

export const DayNamesWeekElement = styled.div<{ isMobile?: boolean }>`
    width: ${({ isMobile }) => (isMobile ? '100%' : `${MONTH_WIDTH}px`)};
    padding: 0 ${getSpacingToken('100')}px;
    box-sizing: border-box;
    display: inline-flex;
    justify-content: space-between;
`;

export const DayNameElement = styled.span`
    width: ${DAY_NAME_SIZE_WIDTH}px;
    text-align: center;
    color: ${getToken('textStaticPrimary')};
`;

export interface CalendarDayProps extends AppliedModifiers {
    numberOfMonths: number;
    isFirstDayOfTheMonth?: boolean;
    isLastDayOfTheMonth?: boolean;
    isEndOfSelection?: boolean;
    enableAdjacentMonthSelection?: boolean;
    isMobile?: boolean;
}

export const CalendarDayElement = styled.div<CalendarDayProps>`
    width: ${({ isMobile }) => (isMobile ? '100%' : `${DAY_SIZE_WIDTH}px`)};
    height: ${getToken('sizing500')}px;
    text-align: center;
    position: relative;

    ${({
        theme,
        inBetweenDate,
        startDate,
        endDate,
        isOutOfMonth,
        numberOfMonths,
        enableAdjacentMonthSelection,
        isStartOfSelection,
        isFirstDayOfTheMonth,
        isEndOfSelection,
        isLastDayOfTheMonth,
    }) =>
        (inBetweenDate || startDate || endDate) &&
        (!isOutOfMonth || (numberOfMonths === 1 && enableAdjacentMonthSelection)) &&
        css`
            &::before {
                content: '';
                position: absolute;
                height: ${getToken('sizing400')}px;
                left: 0;
                right: 0;
                background-color: ${getToken('backgroundInteractiveSecondaryDefault')};

                ${(isStartOfSelection || (isFirstDayOfTheMonth && !enableAdjacentMonthSelection)) &&
                css`
                    border-top-left-radius: ${getToken('sizing400')({ theme }) / 2}px;
                    border-bottom-left-radius: ${getToken('sizing400')({ theme }) / 2}px;
                    left: ${getSpacingToken('200')}px;
                `}

                ${(isEndOfSelection || (isLastDayOfTheMonth && !enableAdjacentMonthSelection)) &&
                css`
                    border-top-right-radius: ${getToken('sizing400')({ theme }) / 2}px;
                    border-bottom-right-radius: ${getToken('sizing400')({ theme }) / 2}px;
                    right: ${getSpacingToken('200')}px;
                `}
            }

            & > ${DayButtonElement} {
                ${getTextTokenPreset('bodySmallStrong')};

                &:disabled {
                    background-color: ${getToken('backgroundDisabled')};
                    color: ${getToken('textDisabled')};
                }

                &:hover:not([disabled]) {
                    background-color: ${getToken('backgroundInteractivePrimaryHover')} !important;
                    border-color: ${getToken('backgroundInteractivePrimaryHover')} !important;
                    color: ${getToken('textStaticInverse')};
                }

                &:active:not([disabled]) {
                    background-color: ${getToken('backgroundInteractivePrimaryActive')} !important;
                    border-color: ${getToken('backgroundInteractivePrimaryActive')} !important;
                    color: ${getToken('textStaticInverse')};
                }
            }
        `}

    ${({ isStartOfSelection, theme }) => css`
        &:last-child::before {
            border-radius: 0 ${getToken('sizing400')({ theme }) / 2}px ${getToken('sizing400')({ theme }) / 2}px 0;
            right: ${getSpacingToken('200')}px;

            ${isStartOfSelection &&
            css`
                border-radius: ${getToken('sizing400')({ theme }) / 2}px;
            `}
        }
    `}

        ${({ isEndOfSelection, theme }) => css`
        &:first-child::before {
            border-radius: ${getToken('sizing400')({ theme }) / 2}px 0 0 ${getToken('sizing400')({ theme }) / 2}px;
            left: ${getSpacingToken('100')}px;

            ${isEndOfSelection &&
            css`
                border-radius: ${getToken('sizing400')({ theme }) / 2}px;
            `}
        }
    `}
`;

export interface DayButtonProps extends AppliedModifiers {
    enableAdjacentMonthSelection?: boolean;
    areSurroundingDaysOfSingleMonthHidden?: boolean;
    isDisabledDayClickable?: boolean;
    isOutOfMonth?: boolean;
    isSingleDaySelectionCircle?: boolean;
    monthSelectorVisible?: boolean;
    numberOfMonths: number;
    singlePicker?: boolean;
}

// TODO find a way how to move style of modifiers out of this component
export const DayButtonElement = styled(Button).attrs({ type: 'submit' })<DayButtonProps>`
    align-items: center;
    cursor: pointer;
    display: inline-flex;
    outline: none;
    justify-content: center;
    width: ${getToken('sizing400')}px;
    height: ${getToken('sizing400')}px;
    margin: 0 calc((${getToken('sizing500')}px - ${getToken('sizing400')}px) / 2)
        calc(${getToken('sizing500')}px - ${getToken('sizing400')}px)
        calc((${getToken('sizing500')}px - ${getToken('sizing400')}px) / 2);
    border-radius: ${getToken('borderRadiusRound')};
    box-sizing: border-box;
    position: relative;
    border: ${getToken('borderWidth100')}px solid transparent;
    background-color: transparent;
    padding: 0;
    ${getTextTokenPreset('bodySmall')}
    color: ${getToken('textStaticPrimary')};

    &:hover:not([disabled]) {
        background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
        border: ${getToken('borderWidth100')}px solid ${getToken('backgroundInteractiveNeutralSubtleHover')};
    }

    &:active:not([disabled]) {
        background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
        border: ${getToken('borderWidth100')}px solid ${getToken('backgroundInteractiveNeutralSubtleActive')};
    }

    ${({ isStartOfSelection, isEndOfSelection, isSingleDaySelectionCircle }) =>
        isStartOfSelection &&
        isEndOfSelection &&
        !isSingleDaySelectionCircle &&
        css`
            border-radius: ${getToken('borderRadiusRound')};
        `}

    ${({ singlePicker }) =>
        singlePicker &&
        css`
            margin: ${getSpacingToken('50')}px;
            padding: 0;
        `}

    ${({ disabled, isOutOfMonth, monthSelectorVisible, isDisabledDayClickable }) =>
        (disabled || isOutOfMonth || monthSelectorVisible || isDisabledDayClickable) &&
        css`
            cursor: auto;
            color: ${getToken('textDisabled')} !important;
        `}

    ${({ disabled, isOutOfMonth, enableAdjacentMonthSelection, isDisabledDayClickable }) =>
        (disabled && !isDisabledDayClickable) || (isOutOfMonth && !enableAdjacentMonthSelection)
            ? css`
                  pointer-events: none;
              `
            : css`
                  cursor: pointer;
              `}

    ${({ isOutOfMonth, numberOfMonths, areSurroundingDaysOfSingleMonthHidden }) =>
        isOutOfMonth &&
        (numberOfMonths > 1 || areSurroundingDaysOfSingleMonthHidden) &&
        css`
            visibility: hidden;
        `}

    ${({ today, isOutOfMonth }) =>
        today &&
        !isOutOfMonth &&
        css`
            border-color: ${getToken('borderInteractiveBoldDefault')};
            ${getTextTokenPreset('bodySmallStrong')};

            &:hover:not([disabled]) {
                border-color: ${getToken('borderInteractiveBoldDefault')};
            }

            &:active:not([disabled]) {
                border-color: ${getToken('borderInteractiveBoldDefault')};
            }
        `}

    ${({ selected, startDate, endDate }) =>
        (selected || startDate || endDate) &&
        css`
            background-color: ${getToken('backgroundInteractivePrimaryDefault')};
            color: ${getToken('textStaticInverse')};
            ${getTextTokenPreset('bodySmallStrong')};

            &:disabled {
                background-color: ${getToken('backgroundDisabled')};
                color: ${getToken('textDisabled')};
            }

            &:focus-visible {
                ${FOCUS_OUTLINE_STYLE}
                background-color: ${getToken('backgroundInteractivePrimaryDefault')};
            }

            &:hover:not([disabled]) {
                background-color: ${getToken('backgroundInteractivePrimaryHover')} !important;
                border-color: ${getToken('backgroundInteractivePrimaryHover')} !important;
                color: ${getToken('textStaticInverse')};
            }

            &:active:not([disabled]) {
                background-color: ${getToken('backgroundInteractivePrimaryActive')} !important;
                border-color: ${getToken('backgroundInteractivePrimaryActive')} !important;
                color: ${getToken('textStaticInverse')};
            }
        `}
`;

export const DayButtonElementWithTooltip = Tooltip(DayButtonElement);

export interface CalendarMonthSelectorProps {
    numberOfWeeks: number;
    monthSelectorVisible?: boolean;
    monthSelectedPosition?: number;
    skipAnimateOut?: boolean;
    standalone?: boolean;
    theme: DefaultTheme;
    isMobile?: boolean;
}

const getTransformHeight = (standalone = false) =>
    standalone ? getMonthSelectorHeightByWeeks : getCalendarHeightByWeeks;

const monthSelectorIn = (
    numberOfWeeks: number,
    theme: DefaultTheme,
    monthSelectedPosition?: number,
    standalone = false
) => keyframes`
    0% { transform: translate(calc(${monthSelectedPosition} * ${MONTH_WIDTH}px + 1px), 0); }
    100% { transform: translate(calc(${monthSelectedPosition} * ${MONTH_WIDTH}px + 1px), calc(${getTransformHeight(
        standalone
    )(numberOfWeeks, theme)}px + 1px)); }
`;

const monthSelectorOut = (
    numberOfWeeks: number,
    theme: DefaultTheme,
    monthSelectedPosition?: number,
    standalone = false
) => keyframes`
    0% { transform: translate(calc(${monthSelectedPosition} * ${MONTH_WIDTH}px + 1px), calc(${getTransformHeight(
        standalone
    )(numberOfWeeks, theme)}px + 1px)); }
    100% { transform: translate(calc(${monthSelectedPosition} * ${MONTH_WIDTH}px + 1px), 0); }
`;

export const CalendarMonthSelectorWrapperElement = styled.div<CalendarMonthSelectorProps>`
    position: absolute;
    top: -${({ numberOfWeeks, theme }) => getMonthSelectorHeightByWeeks(numberOfWeeks, theme)}px;
    overflow: hidden;
    width: ${({ isMobile }) => (isMobile ? '100%' : `calc(${MONTH_WIDTH}px - 1px)`)};
    background-color: ${getToken('backgroundStaticFlat')};
    z-index: 10;
    height: ${({ numberOfWeeks, theme }) => getMonthSelectorHeightByWeeks(numberOfWeeks, theme)}px;

    ${({ monthSelectorVisible, numberOfWeeks, monthSelectedPosition, standalone, skipAnimateOut, theme }) =>
        monthSelectorVisible
            ? css`
                  animation: ${monthSelectorIn(numberOfWeeks, theme, monthSelectedPosition, standalone)}
                      ${ANIMATION_DURATION.fast}ms ${ANIMATION_CURVE.default} 1 forwards;
              `
            : css`
                  animation: ${monthSelectorOut(numberOfWeeks, theme, monthSelectedPosition, standalone)}
                      ${skipAnimateOut ? 0 : ANIMATION_DURATION.fast}ms ${ANIMATION_CURVE.default} 1 forwards;
              `}

    ${({ theme }) => css`
        &::before {
            content: '';
            position: absolute;
            width: 100%;
            height: ${getToken('sizing800')}px;
            background: linear-gradient(
                ${getToken('backgroundStaticFlat')({ theme })},
                ${alpha(getToken('backgroundStaticFlat')({ theme }), getToken('opacity0')({ theme }))}
            );
            pointer-events: none;
        }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            width: 100%;
            height: ${getToken('sizing800')}px;
            background: linear-gradient(
                ${alpha(getToken('backgroundStaticFlat')({ theme }), getToken('opacity0')({ theme }))},
                ${getToken('backgroundStaticFlat')({ theme })}
            );
            pointer-events: none;
        }
    `}
`;

export const ScrollContainerElement = styled.div`
    height: 100%;
    overflow-y: scroll;
    overflow-x: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

export const YearElement = styled.div`
    margin: 0 ${getSpacingToken('200')}px;
    padding: ${getSpacingToken('200')}px 0;
    display: flex;
    border-bottom: ${getToken('borderWidth100')}px solid ${getToken('borderStaticPrimary')};
    ${getTextTokenPreset('bodySmallStrong')};
`;

export const YearLabelElement = styled.div`
    width: ${YEAR_LABEL_WIDTH}px;
    padding-top: ${getSpacingToken('100')}px;
    color: ${getToken('textStaticPrimary')};
`;

export const MonthsContainerElement = styled.div`
    flex: 1;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: repeat(2, 1fr);
    grid-column-gap: ${MONTH_GAP}px;
    grid-row-gap: ${MONTH_GAP}px;
`;

export interface SelectableMonthProps {
    selected?: boolean;
    currentMonth?: boolean;
}

export const SelectableMonthElement = styled(Button).attrs({ type: 'submit' })<SelectableMonthProps>`
    ${getTextTokenPreset('bodySmallStrong')};
    padding: ${getSpacingToken('100')}px;
    width: ${getToken('sizing500')}px;
    height: ${getToken('sizing500')}px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: ${getToken('borderRadiusRound')};
    background-color: transparent;
    border: ${getToken('borderWidth100')}px solid transparent;

    ${(props) =>
        props.disabled &&
        css`
            cursor: auto;
            color: ${getToken('textDisabled')};
        `}

    ${(props) =>
        props.selected &&
        css`
            background-color: ${getToken('backgroundInteractivePrimaryDefault')};
            color: ${getToken('textStaticInverse')};

            &:focus-visible {
                ${FOCUS_OUTLINE_STYLE}
                background-color: ${getToken('backgroundInteractivePrimaryDefault')};
            }
        `}

    &:hover:not([disabled]) {
        ${(props) =>
            !props.selected
                ? css`
                      background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
                      border-color: transparent;
                  `
                : css`
                      background-color: ${getToken('backgroundInteractivePrimaryHover')};
                      border-color: transparent;
                      color: ${getToken('textStaticInverse')};
                  `}
    }

    &:active:not([disabled]) {
        ${(props) =>
            !props.selected
                ? css`
                      background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
                      border-color: transparent;
                  `
                : css`
                      background-color: ${getToken('backgroundInteractivePrimaryActive')};
                      border-color: transparent;
                      color: ${getToken('textStaticInverse')};
                  `}
    }

    ${({ currentMonth }) =>
        currentMonth &&
        css`
            border-color: ${getToken('borderInteractiveBoldDefault')};

            &:hover:not([disabled]) {
                border-color: ${getToken('borderInteractiveBoldDefault')};
            }

            &:active:not([disabled]) {
                border-color: ${getToken('borderInteractiveBoldDefault')};
            }
        `}
`;

export const CalendarFooter = styled(Stack)`
    padding: ${getSpacingToken('200')}px;
`;

export const SectoredCalendarFooter = styled.div<{ small?: boolean }>`
    ${getTextTokenPreset('bodyLargeStrong')};
    color: ${getToken('textStaticPrimary')};

    ${(props) =>
        props.small &&
        css`
            ${getTextTokenPreset('bodySmallStrong')};
        `}
`;

export const CalendarFooterLeftSecondary = styled.span`
    color: ${getToken('textStaticSecondary')};
`;
